import { useEffect } from "react";

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
	"mousemove",
	"mousedown",
	"keydown",
	"scroll",
	"touchstart",
];

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const LS_KEY_LAST_ACTIVITY = "lastActivity";

function getLastActivity(): number | undefined {
	const raw = localStorage.getItem(LS_KEY_LAST_ACTIVITY);
	if (raw === null) {
		return undefined;
	}

	const parsed = Number(raw);
	return Number.isFinite(parsed) ? parsed : undefined;
}

function setLastActivity(timestamp: number) {
	localStorage.setItem(LS_KEY_LAST_ACTIVITY, String(timestamp));
}

/**
 * Clears the persisted last-activity timestamp.
 * Call this on explicit sign-out so a later login does not
 * immediately expire from a stale timestamp.
 */
export function clearLastActivity() {
	localStorage.removeItem(LS_KEY_LAST_ACTIVITY);
}

/**
 * Returns whether the persisted last-activity timestamp is older
 * than the inactivity timeout. Missing or invalid timestamps are
 * treated as not expired.
 */
export function isInactivityExpired(
	timeoutMs: number = INACTIVITY_TIMEOUT_MS,
): boolean {
	const lastActivity = getLastActivity();
	if (lastActivity === undefined) {
		return false;
	}

	return Date.now() - lastActivity >= timeoutMs;
}

/**
 * Signs the user out after a period of inactivity.
 * Activity is detected via mouse, keyboard, scroll, and touch events.
 * The timer resets on each detected interaction.
 * @param onTimeout The function to call when the inactivity timeout is reached.
 * @param timeoutMs The timeout duration in milliseconds. Defaults to 30 minutes.
 * @return A function to clear the inactivity timeout when the component unmounts.
 */
export function useInactivityTimeout(
	onTimeout: () => void,
	timeoutMs: number = INACTIVITY_TIMEOUT_MS,
) {
	//Check if the inactivity timeout has expired on mount
	useEffect(() => {
		if (isInactivityExpired(timeoutMs)) {
			onTimeout();
		}
	}, [onTimeout, timeoutMs]);

	//Listen for continuous activity events of user interaction
	useEffect(() => {
		let timeoutID: ReturnType<typeof setTimeout>;

		function resetTimer() {
			//Reset the last activity timer
			setLastActivity(Date.now());

			//Reset the timeout timer
			clearTimeout(timeoutID);
			timeoutID = setTimeout(() => {
				onTimeout();
			}, timeoutMs);
		}

		resetTimer();

		for (const event of ACTIVITY_EVENTS) {
			document.addEventListener(event, resetTimer, { passive: true });
		}

		return () => {
			clearTimeout(timeoutID);
			for (const event of ACTIVITY_EVENTS) {
				document.removeEventListener(event, resetTimer);
			}
		};
	}, [onTimeout, timeoutMs]);
}
